import { api as generatedApi } from 'gen/rtk-openapi'

const TAG_TYPES = {
  outboundTickets: 'OutboundTickets',
  finishedGoods: 'FinishedGoods',
  packingList: 'PackingList',
  priceSheets: 'PriceSheets',
  inventoryCommodity: 'InventoryCommodity',
  finishedGoodsMeta: 'FinishedGoodsMeta',
  packingListMeta: 'PackingListMeta',
  salesOrders: 'SalesOrders',
  bills: 'Bills',
  invoices: 'Invoices',
}

export const api = generatedApi.enhanceEndpoints({
  addTagTypes: [TAG_TYPES.outboundTickets],
  endpoints: {
    getOutboundTickets: {
      providesTags: result =>
        result && result.Items
          ? // successful query
            [
              ...result.Items.map(outboundTicket => ({
                type: TAG_TYPES.outboundTickets,
                id: outboundTicket.id,
              })),
              { type: TAG_TYPES.outboundTickets, id: 'LIST' },
            ]
          : // error occurred, refetch when `{ type: 'InboundTickets', id: 'LIST' }` is invalidated
            [{ type: TAG_TYPES.outboundTickets, id: 'LIST' }],
    },
    getOutboundTicketsByIds: {}, // POST
    getOutboundTicket: {
      providesTags: (result, error, { outboundTicketId }) => [
        { type: TAG_TYPES.outboundTickets, id: outboundTicketId },
      ],
    },
    createOutboundTicket: {
      invalidatesTags: [
        { type: TAG_TYPES.outboundTickets, id: 'LIST' },
        { type: TAG_TYPES.bills, id: 'LIST' },
        { type: TAG_TYPES.finishedGoods },
        { type: TAG_TYPES.packingList },
        { type: TAG_TYPES.priceSheets },
        { type: TAG_TYPES.inventoryCommodity },
        { type: TAG_TYPES.finishedGoodsMeta },
        { type: TAG_TYPES.packingListMeta },
        { type: TAG_TYPES.salesOrders },
      ],
    },
    updateOutboundTicket: {
      invalidatesTags: (result, error, { outboundTicketId }) => [
        { type: TAG_TYPES.outboundTickets, id: 'LIST' },
        { type: TAG_TYPES.outboundTickets, id: outboundTicketId },
        { type: TAG_TYPES.bills, id: 'LIST' },
        { type: TAG_TYPES.finishedGoods },
        { type: TAG_TYPES.packingList },
        { type: TAG_TYPES.priceSheets },
        { type: TAG_TYPES.inventoryCommodity },
        { type: TAG_TYPES.finishedGoodsMeta },
        { type: TAG_TYPES.packingListMeta },
        { type: TAG_TYPES.salesOrders },
      ],
    },
    voidOutboundTicket: {
      invalidatesTags: (result, error, { outboundTicketId, invoiceId }) => [
        { type: TAG_TYPES.outboundTickets, id: 'LIST' },
        { type: TAG_TYPES.outboundTickets, id: outboundTicketId },
        { type: TAG_TYPES.bills, id: 'LIST' },
        { type: TAG_TYPES.finishedGoods },
        { type: TAG_TYPES.packingList },
        { type: TAG_TYPES.priceSheets },
        { type: TAG_TYPES.inventoryCommodity },
        { type: TAG_TYPES.finishedGoodsMeta },
        { type: TAG_TYPES.packingListMeta },
        { type: TAG_TYPES.salesOrders },
        { type: TAG_TYPES.invoices, id: invoiceId },
        { type: TAG_TYPES.invoices, id: 'LIST' },
      ],
    },
    getPullApartScrapRows: {},
  },
})

export const {
  useGetOutboundTicketsQuery,
  useGetOutboundTicketsByIdsMutation,
  useGetOutboundTicketQuery,
  useCreateOutboundTicketMutation,
  useUpdateOutboundTicketMutation,
  useVoidOutboundTicketMutation,
  useGetPullApartScrapRowsQuery,
} = api

export const getMaterialRowWithYardFields = ({ yardGross = 0, yardTare = 0, um, ...restFields }, defaultYardUm) => ({
  ...restFields,
  gross: Number(yardGross),
  tare: Number(yardTare),
  net: Number(yardGross) - Number(yardTare),
  units: um || defaultYardUm,
})

export const getLooseMaterials = ({ materialTypeId, gross, tare, axleWeights, orderEntryId }) => ({
  materialTypeId,
  tare: Number(tare) || 0,
  gross: Number(gross) || 0,
  weight: Number(gross) - Number(tare),
  axleWeights,
  orderEntryId,
})

export const getFinishedGoodMaterials = row => {
  const { id, orderEntryId, gross, tare, net, units, itemCount } = row
  return {
    id,
    orderEntryId,
    itemCount,
    weight: {
      gross,
      tare,
      net,
      units,
    },
  }
}

export const transformOutboundTicket = outboundTicketData => {
  const {
    customerId,
    customerDisplayName,
    date,
    effectiveDate,
    contractInfo,
    transportationInfo,
    shippingInfo,
    status,
    carrierId,
    notes = [],
    docs = [],
    freightCost,
    salesOrder,
    axleWeights,
    pullApartRows = [],
  } = outboundTicketData
  const freightChargeback = freightCost?.chargeBack || 0

  return {
    consumerInfo: {
      consumerName: {
        id: customerId,
        customerCommonIdentifierString: customerDisplayName,
      },
      dateCreated: new Date(date),
      effectiveDate: effectiveDate ? new Date(effectiveDate) : new Date(date),
      salesOrder: contractInfo?.salesOrder,
      salesOrderId: salesOrder?.id,
      customerPo: contractInfo?.customerPo,
      invoiceWeights: contractInfo?.invoiceWeights,
      bookingRef: contractInfo?.bookingRef,
      millScaleNumber: outboundTicketData?.millScaleNumber,
    },
    transportationInfo: {
      driverName: transportationInfo?.carrier,
      truck: transportationInfo?.carrierNumber,
      trailer: transportationInfo?.trailerNumber,
      carrierLookup: carrierId ? transportationInfo.carrierLookup : null,
      fob: transportationInfo?.fob,
      container: transportationInfo?.container,
      seal: transportationInfo?.seal,
      transportationType: transportationInfo?.transportationType || '',
      export: transportationInfo?.export,
    },
    shippingInfo: {
      locationId: shippingInfo?.locationId,
      shippingAddress: shippingInfo?.address,
      city: shippingInfo?.city,
      state: shippingInfo?.state,
      zip: shippingInfo?.zip,
      name: shippingInfo?.name,
      country: shippingInfo?.country,
    },
    status: {
      value: status === 'AVAILABLE' ? 'INCOMPLETE' : status,
    },
    notes,
    truckWeight: {
      gross: Number(contractInfo?.truckWeight?.gross?.amount || 0),
      tare: Number(contractInfo?.truckWeight?.tare?.amount || 0),
      net: Number(contractInfo?.truckWeight?.net?.amount || 0),
    },
    docs,
    freightCost: {
      cost: freightCost?.cost || 0,
      chargeBack: freightChargeback,
      userSet: freightCost?.userSet || false,
      userChargebackSet: freightCost?.userChargebackSet || false,
      id: freightCost?.id || '',
    },
    axleWeights,
    pullApartRows,
  }
}
